import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useResolve } from '@/infrastructure/useResolve';
import { TOKEN_AccountStore } from './AccountStore';
import { TOKEN_AuthStore } from '@/views/AuthStore';
import { MfaView } from './MfaView';

export const AccountView = observer(function AccountView() {
  const account = useResolve(TOKEN_AccountStore);
  const auth = useResolve(TOKEN_AuthStore);
  // Local, ephemeral form state (the mvvm-view carve-out).
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');

  useEffect(() => {
    void account.load();
  }, [account]);

  return (
    <div className="space-y-6" data-testid="account-screen">
      <section className="rounded-lg border border-slate-200 p-4 dark:border-slate-800">
        <h3 className="mb-2 text-lg font-semibold">Profile</h3>
        <dl className="grid grid-cols-[8rem_1fr] gap-y-1 text-sm">
          <dt className="text-slate-400">Username</dt>
          <dd data-testid="account-username">{auth.username ?? '-'}</dd>
          <dt className="text-slate-400">Name</dt>
          <dd>{auth.name ?? '-'}</dd>
          <dt className="text-slate-400">Email</dt>
          <dd>
            {auth.email ?? '-'}{' '}
            {auth.email && (auth.emailVerified ? <span className="text-emerald-600">✓ verified</span> : <span className="text-amber-600">unverified</span>)}
          </dd>
          <dt className="text-slate-400">Subject</dt>
          <dd className="font-mono text-xs">{auth.sub ?? '-'}</dd>
          <dt className="text-slate-400">Roles</dt>
          <dd>{auth.roles.join(', ') || 'user'}</dd>
        </dl>
      </section>

      <section className="rounded-lg border border-slate-200 p-4 dark:border-slate-800">
        <h3 className="mb-2 text-lg font-semibold">Change password</h3>
        {account.passwordChanged ? (
          <div className="rounded border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-800 dark:bg-emerald-950/30" data-testid="password-changed">
            Password changed. Please{' '}
            <button type="button" onClick={() => void auth.logout()} className="underline">
              sign in again
            </button>
            .
          </div>
        ) : (
          <form
            className="max-w-sm space-y-2"
            onSubmit={(e) => {
              e.preventDefault();
              void account.changePassword(current, next).then(() => {
                if (account.passwordChanged) {
                  setCurrent('');
                  setNext('');
                }
              });
            }}
          >
            <input
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              placeholder="current password"
              data-testid="current-password"
              className="w-full rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-700 dark:bg-slate-900"
            />
            <input
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              placeholder="new password (min 8 chars)"
              data-testid="new-password"
              className="w-full rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-700 dark:bg-slate-900"
            />
            <button type="submit" disabled={account.busy} data-testid="change-password" className="rounded bg-indigo-600 px-3 py-1 text-sm text-white disabled:opacity-50">
              change password
            </button>
            <p className="text-xs text-slate-500">Changing your password signs you out of every session.</p>
          </form>
        )}
        {account.error && <p className="mt-2 text-sm text-rose-600" data-testid="password-error">{account.error}</p>}
      </section>

      <MfaView />

      <section className="rounded-lg border border-slate-200 p-4 dark:border-slate-800">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-lg font-semibold">Active sessions</h3>
          <div className="flex gap-2">
            <button type="button" onClick={() => void account.load()} className="text-xs text-indigo-600 hover:underline">
              refresh
            </button>
            <button type="button" onClick={() => void account.logoutAll()} disabled={account.busy} data-testid="account-logout-all" className="rounded border border-rose-300 px-2 py-0.5 text-xs text-rose-700">
              log out everywhere
            </button>
          </div>
        </div>
        {account.loading && <p className="text-sm text-slate-500">loading…</p>}
        <ul className="divide-y divide-slate-200 text-sm dark:divide-slate-800" data-testid="account-sessions">
          {account.sessions.map((s) => (
            <li key={s.session_id} className="flex items-center justify-between py-2">
              <span className="font-mono text-xs">{s.session_id}</span>
              <button type="button" onClick={() => void account.logoutOne(s.session_id)} disabled={account.busy} className="text-xs text-slate-500 hover:text-rose-600">
                log out
              </button>
            </li>
          ))}
          {!account.loading && account.sessions.length === 0 && <li className="py-2 text-slate-500">No active sessions.</li>}
        </ul>
        {account.notice && <p className="mt-2 text-sm text-emerald-600" data-testid="account-notice">{account.notice}</p>}
        {account.error && <p className="mt-2 text-sm text-rose-600" data-testid="account-error">{account.error}</p>}
      </section>
    </div>
  );
});
